import { useSyncExternalStore } from "react"

// The "closed for maintenance" state of the app (backend: lib/api/maintenance.ts).
//
// The API answers 503 with errorCode 7 (Maintenance) on every route while
// the switch is on — before touching its database — and keeps one route
// open, GET /store/status, which reports the switch. The app never asks
// up front: the first call that comes back 503/Maintenance (usually the
// bootstrap /me) flips this store and App swaps the whole UI for the
// maintenance page, which polls until the system is open again and then
// reloads. Every call site of src/api/api.ts reports through the fetch
// wrapper below, so a flip mid-session is caught by whatever request runs
// next, with no per-call handling.
//
// `admins` mode keeps everyone but Admin-role users out: for them the API
// simply works, so an admin verifying a migration sees the app as usual.

export const MAINTENANCE_ERROR_CODE = 7

const apiUrl = process.env.REACT_APP_API_URL!

export interface MaintenanceState {
  closed: boolean
  mode: "off" | "on" | "admins"
  message: string | null
  retryAfterSecs: number
}

const OPEN: MaintenanceState = { closed: false, mode: "off", message: null, retryAfterSecs: 30 }

let state: MaintenanceState = OPEN
const listeners = new Set<() => void>()

function setState(next: MaintenanceState) {
  state = next
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function useMaintenance(): MaintenanceState {
  return useSyncExternalStore(subscribe, () => state)
}

// the body of a maintenance 503 (or of GET /store/status)
function fromBody(body: any): MaintenanceState | null {
  const m = body?.maintenance
  if (!m || typeof m.closed !== "boolean")
    return null
  return {
    closed: m.closed,
    mode: m.mode === "admins" ? "admins" : m.closed ? "on" : "off",
    message: typeof m.message === "string" ? m.message : null,
    retryAfterSecs: typeof m.retryAfterSecs === "number" && m.retryAfterSecs > 0 ? m.retryAfterSecs : 30,
  }
}

// Notes a response: a maintenance 503 closes the app. Reads a clone, so the
// caller's body stays untouched.
export async function noteMaintenanceResponse(response: Response): Promise<void> {
  if (response.status !== 503)
    return
  const body = await response.clone().json().catch(() => null)
  if (body?.errorCode !== MAINTENANCE_ERROR_CODE)
    return
  setState(fromBody(body) ?? { ...OPEN, closed: true, mode: "on", message: typeof body?.errorMessage === "string" ? body.errorMessage : null })
}

// `fetch` with the maintenance check — src/api/api.ts shadows the global
// with this so every one of its calls reports
export async function maintenanceAwareFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await window.fetch(input, init)
  await noteMaintenanceResponse(response)
  return response
}

// Asks whether the system is open again. GET /store/status answers while
// everything else is closed; in `admins` mode a signed-in admin is let
// through, which only a call on their token can tell — /me is that call
// (it is gated before any query, so polling it while closed costs nothing).
// Resolves true when the app may come back.
export async function probeMaintenance(accessToken?: string): Promise<boolean> {
  try {
    const response = await window.fetch(apiUrl + "/store/status", { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
    if (response.ok) {
      const next = fromBody(await response.json())
      if (next !== null) {
        if (!next.closed) {
          setState(OPEN)
          return true
        }
        setState(next)
        if (next.mode === "admins" && accessToken) {
          const me = await window.fetch(apiUrl + "/me", { headers: { 'Accept': 'application/json', 'Authorization': accessToken }, cache: 'no-store' })
          if (me.ok) {
            setState(OPEN)
            return true
          }
        }
      }
    }
  } catch (err) {
    // offline or the API is being redeployed under us — ask again later
    console.warn("maintenance probe failed", err)
  }
  return false
}
